import React, { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, User, Phone, AlertCircle, Truck, Package, CheckCircle, Eye, EyeOff, MapPin, Building2, Globe, Sparkles } from 'lucide-react';
import { resendVerification } from '../services/api';
import { WhatsAppHelpText } from '../components/WhatsAppButton';
import { DIRECTORY_ENABLED } from '../config/features';

export default function RegisterPage() {
  const { user, loading, register } = useAuth();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next');
  const prefillEmail = searchParams.get('email') || '';
  const prefillPhone = searchParams.get('phone') || '';
  const [formData, setFormData] = useState({
    email: prefillEmail,
    password: '',
    name: '',
    phone: prefillPhone,
    role: 'customer',
    counties: [],
    serves_all_ireland: false,
    main_county: '',
    town: '',
    account_type: 'individual',
    company_name: '',
    business_type: '',
    // Driver compliance
    vehicle_type: '',
    licence_file: '',
    licence_file_back: '',
    insurance_confirmed: false,
    age_confirmed: false,
    terms_accepted: false,
    vat_registered: false,
    vat_number: '',
    // How the driver wants to use JustDeliver — one of:
    //   'bidding'   → bid on customer jobs
    //   'directory' → public advertising profile only
    //   'both'      → both features
    // Required when role === 'driver'. Empty until user picks.
    driver_use_mode: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resending, setResending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32]"></div></div>;
  }

  if (user) {
    const defaultPath = user.role === 'admin' ? '/admin' : user.role === 'driver' ? '/driver' : '/customer';
    const safeNext = nextPath && nextPath.startsWith('/customer') ? nextPath : defaultPath;
    return <Navigate to={safeNext} replace />;
  }

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const selectRole = (role, accountType) => {
    setFormData(prev => ({
      ...prev,
      role: role === 'driver' ? 'driver' : 'customer',
      account_type: accountType,
      // Reset conditional fields
      counties: role === 'driver' ? prev.counties : [],
      town: role === 'driver' ? prev.town : '',
      company_name: accountType === 'business' ? prev.company_name : '',
      business_type: accountType === 'business' ? prev.business_type : ''
    }));
  };

  const toggleCounty = (county) => {
    setError('');
    setFormData(prev => {
      const has = prev.counties.includes(county);
      if (has) {
        // If they un-tick the county that was their main county, clear main too.
        const next = prev.counties.filter(c => c !== county);
        return {
          ...prev,
          counties: next,
          main_county: prev.main_county === county ? '' : prev.main_county,
        };
      }
      return { ...prev, counties: [...prev.counties, county] };
    });
  };

  const toggleServesAllIreland = () => {
    setError('');
    setFormData(prev => ({
      ...prev,
      serves_all_ireland: !prev.serves_all_ireland,
      counties: !prev.serves_all_ireland ? [] : prev.counties // clear specific counties when opting in
    }));
  };

  const IRISH_COUNTIES = [
    "Dublin", "Cork", "Galway", "Limerick", "Waterford", "Kildare", "Meath",
    "Wicklow", "Wexford", "Kilkenny", "Clare", "Kerry", "Mayo", "Donegal",
    "Sligo", "Leitrim", "Roscommon", "Longford", "Westmeath", "Offaly",
    "Laois", "Carlow", "Tipperary", "Monaghan", "Cavan", "Louth",
    "Fermanagh", "Tyrone", "Derry", "Antrim", "Down", "Armagh"
  ];

  const handleLicenceUpload = async (e, side = 'front') => {
    setError('');
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError('Licence image must be under 5 MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please upload a JPG or PNG image of your licence.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const key = side === 'back' ? 'licence_file_back' : 'licence_file';
      setFormData(prev => ({ ...prev, [key]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const selectedType = formData.account_type === 'business' ? 'business' : formData.role === 'driver' ? 'driver' : 'individual';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (selectedType === 'driver') {
      if (!formData.driver_use_mode && DIRECTORY_ENABLED) {
        setError('Please choose how you want to use JustDeliver (Bidding, Public Profile, or Both).');
        return;
      }
      if (!formData.serves_all_ireland && formData.counties.length === 0) {
        setError('Please select at least 1 county or tick "Available anywhere in Ireland".');
        return;
      }
      if (!formData.main_county) {
        setError('Please pick your main county (where you\'re based).');
        return;
      }
      if (!formData.serves_all_ireland && !formData.counties.includes(formData.main_county)) {
        setError('Your main county must be one of the counties you cover.');
        return;
      }
      if (!formData.vehicle_type) {
        setError('Please select your vehicle type.');
        return;
      }
      if (!formData.licence_file) {
        setError('Please upload a clear photo of your driving licence.');
        return;
      }
      if (!formData.insurance_confirmed) {
        setError('Please confirm your motor insurance covers delivery/courier work.');
        return;
      }
      if (!formData.age_confirmed) {
        setError('Please confirm you are over 18 and legally permitted to drive in Ireland.');
        return;
      }
      if (!formData.terms_accepted) {
        setError('Please accept the Terms & Agreement to continue.');
        return;
      }
      if (formData.vat_registered && !formData.vat_number.trim()) {
        setError('Please enter your VAT number, or untick "VAT registered".');
        return;
      }
    }
    setIsLoading(true);
    try {
      const result = await register({
        ...formData,
        driver_use_mode: formData.driver_use_mode || (selectedType === 'driver' ? 'bidding' : formData.driver_use_mode),
      });
      if (!result || !result.success) {
        setError(result?.error || 'Registration failed');
        return;
      }
      // Persist the Quick Post claim redirect across the email-verification
      // round-trip — RegisterPage doesn't auto-login, so VerifyEmailPage
      // picks this up after the user clicks the email link.
      if (nextPath) {
        try { localStorage.setItem('postSignupRedirect', nextPath); } catch (e) { /* storage disabled */ }
      }
      setRegistered(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification(formData.email);
    } catch (e) {}
    setResending(false);
  };

  if (registered) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md text-center space-y-6">
          <div className="flex justify-center">
            <img src="/logo.png" alt="JustDeliver" className="h-12 w-auto" />
          </div>
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-[#2E7D32]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Check your email</h2>
          <p className="text-gray-600">We've sent a verification link to <strong>{formData.email}</strong></p>
          <p className="text-sm text-gray-500">Click the link to activate your account. Check spam if you don't see it.</p>
          <Button onClick={handleResend} disabled={resending} variant="outline" className="w-full" data-testid="resend-verification-btn">
            {resending ? 'Sending...' : 'Resend Verification Email'}
          </Button>
          <Link to="/login" className="block text-sm text-[#2E7D32] font-semibold hover:underline">Back to Login</Link>
        </div>
      </div>
    );
  }

  const roles = [
    { key: 'individual', role: 'customer', accountType: 'individual', icon: Package, label: 'Individual', desc: 'Post one-off deliveries', color: '#2E7D32', bg: 'green' },
    { key: 'driver', role: 'driver', accountType: 'driver', icon: Truck, label: 'Driver', desc: 'Earn by completing deliveries', color: '#F57C00', bg: 'orange' },
    { key: 'business', role: 'customer', accountType: 'business', icon: Building2, label: 'Business', desc: 'Post regular or bulk deliveries', color: '#1565C0', bg: 'blue' },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 sm:p-8">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <Link to="/"><img src="/logo.png" alt="JustDeliver" className="h-10 w-auto" /></Link>
        </div>

        <Card className="border border-gray-200 shadow-sm" data-testid="register-card">
          <CardHeader className="pb-2 text-center">
            <CardTitle className="text-xl font-bold">Create your account</CardTitle>
            <CardDescription>Join JustDeliver in under a minute</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="register-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
              )}

              {/* Role selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">I am a:</Label>
                <div className="grid grid-cols-3 gap-2">
                  {roles.map(({ key, role, accountType, icon: Icon, label, desc, color, bg }) => {
                    const active = selectedType === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => selectRole(role, accountType)}
                        className={`flex flex-col items-center gap-1.5 p-3 border rounded-lg cursor-pointer transition-all text-center
                          ${active
                            ? `border-[${color}] bg-${bg}-50 ring-1 ring-[${color}]/30`
                            : 'border-gray-200 hover:border-gray-300'
                          }`}
                        style={active ? { borderColor: color, backgroundColor: `${color}0D` } : {}}
                        data-testid={`register-role-${key}`}
                      >
                        <Icon className="w-5 h-5" style={{ color: active ? color : '#9CA3AF' }} />
                        <span className={`text-sm font-semibold ${active ? 'text-gray-900' : 'text-gray-600'}`}>{label}</span>
                        <span className="text-[10px] leading-tight text-gray-400">{desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Common fields */}
              <div className="space-y-1">
                <Label htmlFor="name" className="text-xs text-gray-500">{selectedType === 'business' ? 'Contact Name' : 'Full Name'}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="name" name="name" placeholder={selectedType === 'business' ? 'Your name' : 'Full name'} value={formData.name} onChange={handleChange} className="pl-10 rounded-sm" required data-testid="register-name-input" />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="email" className="text-xs text-gray-500">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="email" name="email" type="email" placeholder="you@email.com" value={formData.email} onChange={handleChange} className="pl-10 rounded-sm" required data-testid="register-email-input" />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="phone" className="text-xs text-gray-500">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="phone" name="phone" type="tel" placeholder="08X XXX XXXX" value={formData.phone} onChange={handleChange} className="pl-10 rounded-sm" required data-testid="register-phone-input" />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="password" className="text-xs text-gray-500">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={formData.password} onChange={handleChange} className="pl-10 pr-10 rounded-sm" required minLength={6} data-testid="register-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="register-password-toggle" tabIndex={-1}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Driver-specific: how do you want to use JustDeliver?
                  Hidden while the public directory is switched off — those
                  drivers are bidding-only, set automatically on submit. */}
              {selectedType === 'driver' && DIRECTORY_ENABLED && (
                <div className="space-y-3" data-testid="driver-use-mode-block">
                  <div>
                    <Label className="text-sm font-semibold text-gray-900">How would you like to use JustDeliver?</Label>
                    <p className="text-xs text-gray-500 mt-0.5">Choose the option that best suits you. You can change this later from your account.</p>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      {
                        key: 'bidding',
                        icon: <Truck className="w-5 h-5" />,
                        title: 'Bid on delivery jobs',
                        desc: 'I want to bid on jobs posted by customers. Find jobs, submit bids and win deliveries.',
                      },
                      {
                        key: 'directory',
                        icon: <Globe className="w-5 h-5" />,
                        title: 'Public driver profile',
                        desc: 'I just want a public profile so customers can find and contact me directly. No bidding, no payout setup.',
                        directoryOnly: true,
                      },
                      {
                        key: 'both',
                        icon: <Sparkles className="w-5 h-5" />,
                        title: 'Both',
                        desc: 'Bid on customer jobs AND have a public profile customers can find and contact directly.',
                        recommended: true,
                        directoryOnly: true,
                      },
                    ].filter((opt) => DIRECTORY_ENABLED || !opt.directoryOnly).map((opt) => {
                      const active = formData.driver_use_mode === opt.key;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          data-testid={`register-use-mode-${opt.key}`}
                          onClick={() => setFormData((prev) => ({ ...prev, driver_use_mode: opt.key }))}
                          className={`flex items-start gap-3 p-3 border rounded-lg text-left transition-all ${
                            active
                              ? 'border-[#2E7D32] bg-emerald-50 ring-2 ring-[#2E7D32]/20'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center ${
                            active ? 'bg-[#2E7D32] text-white' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {opt.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-sm font-semibold ${active ? 'text-[#1B5E20]' : 'text-gray-900'}`}>{opt.title}</span>
                              {opt.recommended && !active && (
                                <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Recommended</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-1 leading-snug">{opt.desc}</p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center mt-1 ${
                            active ? 'border-[#2E7D32] bg-[#2E7D32]' : 'border-gray-300 bg-white'
                          }`}>
                            {active && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Driver-specific: counties (multi-select, 1+ required) + All-Ireland opt-in + optional town */}
              {selectedType === 'driver' && (
                <div className="space-y-3 p-3 bg-emerald-50/40 border border-emerald-100 rounded-lg animate-in fade-in duration-200" data-testid="driver-counties-block">
                  {/* All-Ireland master toggle */}
                  <label
                    className={[
                      'flex items-start gap-2 p-2.5 border rounded-md cursor-pointer transition-colors',
                      formData.serves_all_ireland
                        ? 'bg-[#2E7D32] border-[#2E7D32] text-white'
                        : 'bg-white border-gray-200 hover:border-[#2E7D32]'
                    ].join(' ')}
                    data-testid="serves-all-ireland-toggle"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-white"
                      checked={formData.serves_all_ireland}
                      onChange={toggleServesAllIreland}
                      data-testid="serves-all-ireland-checkbox"
                    />
                    <span className="text-xs">
                      <span className="font-semibold">Available anywhere in Ireland</span>
                      <span className={`block text-[11px] mt-0.5 ${formData.serves_all_ireland ? 'text-emerald-50' : 'text-gray-500'}`}>
                        Tick this if you can deliver to any county. Otherwise pick the specific counties below.
                      </span>
                    </span>
                  </label>

                  <div className={formData.serves_all_ireland ? 'opacity-40 pointer-events-none' : ''}>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-gray-500 flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-gray-500" /> Counties you can deliver in
                          {!formData.serves_all_ireland && <span className="text-red-500">*</span>}
                        </Label>
                        <span className="text-[11px] text-gray-500" data-testid="counties-counter">
                          {formData.counties.length} selected
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500">Pick as many as you like.</p>
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="counties-grid">
                        {IRISH_COUNTIES.map(c => {
                          const selected = formData.counties.includes(c);
                          return (
                            <button
                              key={c}
                              type="button"
                              onClick={() => toggleCounty(c)}
                              data-testid={`county-pill-${c}`}
                              data-selected={selected}
                              className={[
                                'text-xs px-2.5 py-1 rounded-full border transition-colors',
                                selected
                                  ? 'bg-[#2E7D32] text-white border-[#2E7D32]'
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-[#2E7D32] hover:text-[#2E7D32]'
                              ].join(' ')}
                            >
                              {c}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="main_county" className="text-xs text-gray-500 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5 text-[#F57C00]" /> Main county (where you're based) <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-[11px] text-gray-500">
                      {formData.serves_all_ireland
                        ? 'Pick any Irish county — this is just your home base.'
                        : 'Must be one of the counties you ticked above.'}
                    </p>
                    <select
                      id="main_county"
                      name="main_county"
                      value={formData.main_county}
                      onChange={handleChange}
                      className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30 focus:border-[#2E7D32]"
                      data-testid="register-main-county-select"
                    >
                      <option value="">Select your main county…</option>
                      {(formData.serves_all_ireland ? IRISH_COUNTIES : formData.counties).map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="town" className="text-xs text-gray-500">Town / Area (optional)</Label>
                    <Input
                      id="town" name="town"
                      placeholder="e.g. Swords, Tramore, Castletroy"
                      value={formData.town}
                      onChange={handleChange}
                      className="rounded-sm bg-white"
                      data-testid="register-town-input"
                    />
                  </div>
                </div>
              )}

              {/* Driver-specific: Compliance (vehicle, licence, declarations, VAT) */}
              {selectedType === 'driver' && (
                <div className="space-y-3 p-3 bg-amber-50/40 border border-amber-200 rounded-lg" data-testid="driver-compliance-block">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-900">
                      <span className="font-semibold">Quick compliance check (under 2 minutes)</span>
                      <span className="block text-[11px] text-amber-800 mt-0.5">Your licence will be reviewed before activation.</span>
                    </p>
                  </div>

                  {/* Vehicle type */}
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-500">Vehicle Type <span className="text-red-500">*</span></Label>
                    <div className="flex gap-2" data-testid="vehicle-type-group">
                      {['Car', 'Van', 'Truck'].map(v => {
                        const val = v.toLowerCase();
                        const sel = formData.vehicle_type === val;
                        return (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setFormData(p => ({ ...p, vehicle_type: val }))}
                            data-testid={`vehicle-type-${val}`}
                            className={`flex-1 py-2 text-sm rounded-md border transition-colors ${sel ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#2E7D32]'}`}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Licence upload — front + back */}
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Driving Licence — Front <span className="text-red-500">*</span></Label>
                    <label
                      htmlFor="licence-file-input"
                      className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-dashed border-gray-300 rounded-md cursor-pointer hover:border-[#2E7D32] text-sm text-gray-600 hover:text-[#2E7D32]"
                      data-testid="licence-upload-label"
                    >
                      {formData.licence_file ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-[#2E7D32]" />
                          <span className="text-[#2E7D32] font-medium">Front uploaded — tap to replace</span>
                        </>
                      ) : (
                        <span>Upload a clear photo of the FRONT of your licence (JPG/PNG, ≤5 MB)</span>
                      )}
                    </label>
                    <input
                      id="licence-file-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleLicenceUpload(e, 'front')}
                      data-testid="licence-file-input"
                    />

                    <Label className="text-xs text-gray-500 pt-1 block">Driving Licence — Back <span className="text-gray-400 font-normal">(shows vehicle categories)</span></Label>
                    <label
                      htmlFor="licence-file-back-input"
                      className="flex items-center justify-center gap-2 px-3 py-2.5 bg-white border border-dashed border-gray-300 rounded-md cursor-pointer hover:border-[#2E7D32] text-sm text-gray-600 hover:text-[#2E7D32]"
                      data-testid="licence-back-upload-label"
                    >
                      {formData.licence_file_back ? (
                        <>
                          <CheckCircle className="w-4 h-4 text-[#2E7D32]" />
                          <span className="text-[#2E7D32] font-medium">Back uploaded — tap to replace</span>
                        </>
                      ) : (
                        <span>Upload a clear photo of the BACK of your licence (JPG/PNG, ≤5 MB)</span>
                      )}
                    </label>
                    <input
                      id="licence-file-back-input"
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleLicenceUpload(e, 'back')}
                      data-testid="licence-file-back-input"
                    />
                    <p className="text-[11px] text-gray-500">Tap each box to use your camera or pick from your gallery. Make sure the licence isn't expired. Reviewed before activation.</p>
                  </div>

                  {/* Driver Insurance Declaration — legal cover, no upload required.
                      Admin can request proof at any time via the admin dashboard. */}
                  <div className="p-3 bg-white border border-indigo-200 rounded-md">
                    <div className="text-sm font-semibold text-indigo-900 mb-1">Driver Insurance Declaration</div>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 w-4 h-4 accent-[#2E7D32]"
                        checked={formData.insurance_confirmed}
                        onChange={(e) => setFormData(p => ({ ...p, insurance_confirmed: e.target.checked }))}
                        data-testid="insurance-confirmed-checkbox"
                      />
                      <span className="text-xs text-gray-700 leading-relaxed">
                        I confirm that I hold, and will maintain, all insurance required by law to carry out paid delivery work in Ireland. I understand that I am solely responsible for ensuring my insurance remains valid and appropriate for the work I undertake through JustDeliver. I also understand that JustDeliver may request proof of insurance at any time, and failure to provide it may result in my account being suspended or terminated.
                      </span>
                    </label>
                    <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                      JustDeliver is a technology platform connecting independent drivers with customers. Drivers are responsible for ensuring they have the appropriate insurance required for the services they provide.
                    </p>
                  </div>

                  {/* Age & legal eligibility */}
                  <label className="flex items-start gap-2 p-2 bg-white border border-gray-200 rounded-md cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 accent-[#2E7D32]"
                      checked={formData.age_confirmed}
                      onChange={(e) => setFormData(p => ({ ...p, age_confirmed: e.target.checked }))}
                      data-testid="age-confirmed-checkbox"
                    />
                    <span className="text-xs text-gray-700">
                      I confirm I am over 18 and legally permitted to drive in Ireland.
                    </span>
                  </label>

                  {/* Terms & agreement (with key points) */}
                  <label className="flex items-start gap-2 p-2 bg-white border border-gray-200 rounded-md cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 accent-[#2E7D32]"
                      checked={formData.terms_accepted}
                      onChange={(e) => setFormData(p => ({ ...p, terms_accepted: e.target.checked }))}
                      data-testid="terms-accepted-checkbox"
                    />
                    <span className="text-xs text-gray-700">
                      <span className="font-semibold">I agree to the Terms & Agreement.</span>
                      <span className="block text-[11px] text-gray-500 mt-1 leading-relaxed">
                        • Drivers are self-employed (independent contractors).<br />
                        • Drivers are responsible for their own licence, insurance and taxes.<br />
                        • JustDeliver acts as a platform connecting customers with drivers.<br />
                        • JustDeliver is not liable for driver actions during deliveries.<br />
                        • JustDeliver may remove drivers for repeated poor performance or complaints.
                      </span>
                    </span>
                  </label>

                  {/* VAT registered */}
                  <div className="space-y-1.5 p-2 bg-white border border-gray-200 rounded-md">
                    <Label className="text-xs text-gray-700 font-medium">Are you VAT registered?</Label>
                    <div className="flex gap-2" data-testid="vat-toggle-group">
                      {[{ k: false, l: 'No' }, { k: true, l: 'Yes' }].map(({ k, l }) => {
                        const sel = formData.vat_registered === k;
                        return (
                          <button
                            key={l}
                            type="button"
                            onClick={() => setFormData(p => ({ ...p, vat_registered: k, vat_number: k ? p.vat_number : '' }))}
                            data-testid={`vat-${l.toLowerCase()}-btn`}
                            className={`flex-1 py-1.5 text-sm rounded-md border ${sel ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#2E7D32]'}`}
                          >
                            {l}
                          </button>
                        );
                      })}
                    </div>
                    {formData.vat_registered && (
                      <Input
                        placeholder="VAT number (e.g. IE1234567X)"
                        value={formData.vat_number}
                        onChange={(e) => setFormData(p => ({ ...p, vat_number: e.target.value.toUpperCase() }))}
                        className="rounded-sm text-sm"
                        data-testid="vat-number-input"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Business-specific fields */}
              {selectedType === 'business' && (
                <div className="space-y-3 p-3 bg-blue-50/50 border border-blue-100 rounded-lg animate-in fade-in duration-200" data-testid="business-fields">
                  <div className="space-y-1">
                    <Label htmlFor="company_name" className="text-xs text-gray-500">Company Name</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input id="company_name" name="company_name" placeholder="Your business name" value={formData.company_name} onChange={handleChange} className="pl-10 rounded-sm bg-white" required data-testid="register-company-input" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="business_type" className="text-xs text-gray-500">Business Type (optional)</Label>
                    <select id="business_type" name="business_type" value={formData.business_type} onChange={handleChange} className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm bg-white" data-testid="register-business-type">
                      <option value="">Select type</option>
                      <option value="furniture_seller">Furniture Seller</option>
                      <option value="retail_shop">Retail Shop</option>
                      <option value="marketplace_seller">Marketplace Seller</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              )}

              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={agreedTerms} onChange={(e) => setAgreedTerms(e.target.checked)} required className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-[#2E7D32]" data-testid="register-terms-checkbox" />
                <span className="text-xs text-gray-600">
                  I agree to the <a href="/terms" target="_blank" className="text-[#2E7D32] font-medium hover:underline">Terms & Conditions</a> and <a href="/privacy" target="_blank" className="text-[#2E7D32] font-medium hover:underline">Privacy Policy</a>
                </span>
              </label>

              <Button
                type="submit"
                className={`w-full rounded-sm h-11 text-white ${
                  selectedType === 'driver' ? 'bg-[#F57C00] hover:bg-[#E65100]' :
                  selectedType === 'business' ? 'bg-[#1565C0] hover:bg-[#0D47A1]' :
                  'bg-[#2E7D32] hover:bg-[#1B5E20]'
                }`}
                disabled={isLoading}
                data-testid="register-submit-btn"
              >
                {isLoading ? 'Creating account...' : 'Create Account'}
              </Button>

              <p className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#2E7D32] font-semibold hover:underline" data-testid="register-login-link">Sign in</Link>
              </p>
              <WhatsAppHelpText />
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
